import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { secretApi, type TenantSecret } from '@/lib/api';
import { Lock, Plus, Pencil, Trash2, X, Info } from 'lucide-react';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { SettingsEmptyState } from '@/components/SettingsEmptyState';
import { PageGuide } from '@/components/PageGuide';
import styles from './_auth.settings.secrets.module.css';

export const Route = createFileRoute('/_auth/settings/secrets')({
  component: SecretsPage,
});

function normalizeName(raw: string): string {
  return raw
    .toUpperCase()
    .replace(/\s+/g, '_')
    .replace(/[^A-Z0-9_]/g, '');
}

export default function SecretsPage() {
  const [secrets, setSecrets] = useState<TenantSecret[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState<'create' | 'edit' | null>(null);
  const [editTarget, setEditTarget] = useState<TenantSecret | null>(null);
  const [form, setForm] = useState({ name: '', value: '', description: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TenantSecret | null>(null);

  const load = () => {
    secretApi.list().then(setSecrets).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  function openCreate() {
    setForm({ name: '', value: '', description: '' });
    setError(null);
    setShowModal('create');
  }

  function openEdit(secret: TenantSecret) {
    setEditTarget(secret);
    setForm({ name: secret.name, value: '', description: secret.description ?? '' });
    setError(null);
    setShowModal('edit');
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      if (showModal === 'create') {
        await secretApi.create({
          name: normalizeName(form.name),
          value: form.value,
          description: form.description || undefined,
        });
      } else if (editTarget) {
        const body: { value?: string; description?: string } = {};
        if (form.value) body.value = form.value;
        body.description = form.description || undefined;
        await secretApi.update(editTarget.name, body);
      }
      setShowModal(null);
      setEditTarget(null);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Operation failed');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(secret: TenantSecret) {
    await secretApi.delete(secret.name);
    setDeleteTarget(null);
    load();
  }

  const isCreateValid = normalizeName(form.name).length >= 2 && form.value.length > 0;
  const isEditValid = form.value.length > 0 || form.description !== (editTarget?.description ?? '');

  return (
    <div className={styles.page}>
      <PageGuide pageKey="secrets" />

      <div className={styles.header}>
        <div>
          <h1 className={styles.title}><Lock size={22} /> Secret Manager</h1>
          <p className={styles.subtitle}>
            Encrypted secrets for your tools — referenced as <code>{'{{secrets.NAME}}'}</code> in tool configs.
          </p>
        </div>
        <button id="create-secret" className={styles.createBtn} onClick={openCreate}>
          <Plus size={16} /> New Secret
        </button>
      </div>

      {/* Usage hint */}
      <div className={styles.usageHint}>
        <Info size={16} className={styles.usageHintIcon} />
        <div className={styles.usageHintText}>
          Reference secrets in HTTP tool headers, URLs, or code tools using{' '}
          <code className={styles.usageHintCode}>{'{{secrets.YOUR_KEY}}'}</code>
          {' '}— they are resolved at execution time and never appear in logs.
        </div>
      </div>

      <div className={styles.table}>
        <div className={styles.thead}>
          <div className={styles.theadCell}>Name</div>
          <div className={styles.theadCell}>Description</div>
          <div className={styles.theadCell}>Last Updated</div>
          <div className={styles.theadCell}></div>
        </div>

        {loading && [1, 2].map(i => <div key={i} className={`${styles.row} ${styles.skeleton}`} />)}

        {!loading && secrets.length === 0 && (
          <SettingsEmptyState
            icons={[
              { icon: <Lock size={18} />, color: '#6366f1' },
            ]}
            title="No secrets yet"
            description="Create a secret to securely store API keys, tokens, or credentials. Reference them in tools using {{secrets.NAME}} syntax."
            buttonLabel="Create Secret"
            onAction={openCreate}
          />
        )}

        {!loading && secrets.map(secret => (
          <div key={secret.id} className={styles.row}>
            <div className={styles.cell}>
              <span className={styles.secretName}>{secret.name}</span>
            </div>
            <div className={styles.cell}>
              {secret.description ? (
                <span>{secret.description}</span>
              ) : (
                <span className={styles.cellSub}>—</span>
              )}
            </div>
            <div className={styles.cell}>
              <span className={styles.cellSub}>
                {new Date(secret.updatedAt).toLocaleDateString(undefined, {
                  month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                })}
              </span>
            </div>
            <div className={styles.cell}>
              <div className={styles.actions}>
                <button className={styles.actionBtn} onClick={() => openEdit(secret)} title="Update secret">
                  <Pencil size={14} />
                </button>
                <button className={styles.deleteBtn} onClick={() => setDeleteTarget(secret)} title="Delete secret">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Create / Edit modal */}
      {showModal && (
        <div className={styles.overlay} onClick={e => { if (e.target === e.currentTarget) { setShowModal(null); setEditTarget(null); } }}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>{showModal === 'create' ? 'Create Secret' : `Update "${editTarget?.name}"`}</h2>
              <button className={styles.dismissBtn} onClick={() => { setShowModal(null); setEditTarget(null); }}><X size={16} /></button>
            </div>
            <div className={styles.modalBody}>
              {showModal === 'create' && (
                <div className={styles.field}>
                  <label className={styles.label}>Secret Name <span className={styles.req}>*</span></label>
                  <input
                    id="secret-name"
                    className={styles.inputMono}
                    placeholder="e.g. STRIPE_API_KEY"
                    value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: normalizeName(e.target.value) }))}
                    autoFocus
                  />
                  <span className={styles.fieldHint}>UPPER_SNAKE_CASE — auto-formatted as you type</span>
                </div>
              )}
              <div className={styles.field}>
                <label className={styles.label}>
                  Secret Value {showModal === 'create' && <span className={styles.req}>*</span>}
                </label>
                <input
                  id="secret-value"
                  type="password"
                  className={styles.input}
                  placeholder={showModal === 'edit' ? 'Leave empty to keep current value' : 'sk-live-...'}
                  value={form.value}
                  onChange={e => setForm(f => ({ ...f, value: e.target.value }))}
                  autoFocus={showModal === 'edit'}
                  autoComplete="off"
                />
                {showModal === 'edit' && (
                  <span className={styles.fieldHint}>Leave empty to keep the existing value.</span>
                )}
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Description</label>
                <input
                  className={styles.input}
                  placeholder="What is this secret used for?"
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                />
              </div>
              {error && (
                <div style={{ padding: '0.5rem 0.75rem', borderRadius: 8, background: 'var(--color-error-dim)', color: 'var(--color-error)', fontSize: '0.8125rem' }}>
                  {error}
                </div>
              )}
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.cancelBtn} onClick={() => { setShowModal(null); setEditTarget(null); }}>Cancel</button>
              <button
                id="confirm-secret"
                className={styles.confirmBtn}
                onClick={handleSave}
                disabled={showModal === 'create' ? !isCreateValid || saving : !isEditValid || saving}
              >
                {saving ? 'Saving…' : showModal === 'create' ? 'Create Secret' : 'Update Secret'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && handleDelete(deleteTarget)}
        title="Delete Secret"
        description={`This will permanently delete "${deleteTarget?.name}". Any tools referencing {{secrets.${deleteTarget?.name}}} will fail until a new value is provided.`}
        confirmLabel="Delete Secret"
        variant="danger"
      />
    </div>
  );
}
